import { useEffect, useState } from 'react';
import { listPhotos, type Photo } from '../api/photos';
import { PhotoUploader } from './PhotoUploader';
import { PhotoLightbox } from './PhotoLightbox';

export function PhotoGallery({ tripId }: { tripId: string }) {
  const [photos, setPhotos] = useState<Photo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  useEffect(() => {
    listPhotos(tripId)
      .then(setPhotos)
      .catch((e: Error) => setError(e.message ?? 'Errore caricamento foto'));
  }, [tripId]);

  function onUploaded(p: Photo) {
    setPhotos((prev) => (prev ? sortPhotos([...prev, p]) : [p]));
  }

  function onDeleted(id: string) {
    setPhotos((prev) => (prev ? prev.filter((p) => p.id !== id) : prev));
  }

  function onCaptionChanged(updated: Photo) {
    setPhotos((prev) =>
      prev ? prev.map((p) => (p.id === updated.id ? updated : p)) : prev,
    );
  }

  return (
    <section className="photos-section">
      <div className="section-head">
        <h2>Foto</h2>
        {photos && <span className="muted">{photos.length}</span>}
      </div>

      <PhotoUploader tripId={tripId} onUploaded={onUploaded} />

      {error && <p className="err">{error}</p>}
      {!photos && !error && <p className="muted">Caricamento foto…</p>}

      {photos && photos.length > 0 && (
        <ul className="photo-grid">
          {photos.map((p, i) => (
            <li key={p.id} className="photo-thumb" onClick={() => setLightboxIdx(i)}>
              <img src={p.url} alt={p.caption ?? ''} loading="lazy" />
              {p.caption && <span className="thumb-caption">{p.caption}</span>}
            </li>
          ))}
        </ul>
      )}

      {lightboxIdx !== null && photos && (
        <PhotoLightbox
          photos={photos}
          startIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onDeleted={onDeleted}
          onCaptionChanged={onCaptionChanged}
        />
      )}
    </section>
  );
}

function sortPhotos(arr: Photo[]): Photo[] {
  return [...arr].sort((a, b) => {
    const aT = a.takenAt ?? a.createdAt;
    const bT = b.takenAt ?? b.createdAt;
    return aT.localeCompare(bT);
  });
}
